import { useEffect, useState } from 'react';
import { X, CheckSquare, Square, Loader2 } from 'lucide-react';
import { getTaskChecklist, toggleChecklistItem, TaskChecklistWithItems } from '../../lib/checklists';

interface Props {
    taskId: string;
    onClose: () => void;
    onChecklistUpdate?: (isComplete: boolean) => void;
}

export function ViewChecklistModal({ taskId, onClose, onChecklistUpdate }: Props) {
    const [checklist, setChecklist] = useState<TaskChecklistWithItems | null>(null);
    const [loading, setLoading] = useState(true);
    const [togglingItems, setTogglingItems] = useState<Set<string>>(new Set());

    useEffect(() => {
        loadChecklist();
    }, [taskId]);

    const loadChecklist = async () => {
        try {
            setLoading(true);
            const data = await getTaskChecklist(taskId);
            setChecklist(data);
            if (data) {
                const allComplete = data.items.every(item => item.completion);
                onChecklistUpdate?.(allComplete);
            }
        } catch (error) {
            console.error('Failed to load checklist:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleItem = async (itemId: string) => {
        if (togglingItems.has(itemId) || !checklist) return;

        try {
            setTogglingItems(prev => new Set([...prev, itemId]));
            await toggleChecklistItem(taskId, itemId, null);
            
            // Update local state optimistically
            const updatedChecklist = {
                ...checklist,
                items: checklist.items.map(item => {
                    if (item.id === itemId) {
                        return {
                            ...item,
                            completion: item.completion ? undefined : { 
                                id: 'temp', 
                                item_id: itemId,
                                completed_at: new Date().toISOString(),
                                completed_by: null
                            }
                        };
                    }
                    return item;
                })
            };
            setChecklist(updatedChecklist);

            // Update parent about completion status
            const allComplete = updatedChecklist.items.every(item => item.completion);
            onChecklistUpdate?.(allComplete);

        } catch (error) {
            console.error('Failed to toggle checklist item:', error);
            // Revert on error
            loadChecklist();
        } finally {
            setTogglingItems(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="flex justify-center items-center h-32">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                </div>
            </div>
        );
    }

    if (!checklist) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Checklist Not Found</h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    <p className="text-gray-600">This task doesn't have a checklist attached.</p>
                </div>
            </div>
        );
    }

    const completedItems = checklist.items.filter(item => item.completion).length;
    const progress = (completedItems / checklist.items.length) * 100;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">{checklist.name}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{completedItems} of {checklist.items.length} completed</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    {checklist.items.map((item) => (
                        <div
                            key={item.id}
                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-md cursor-pointer"
                            onClick={() => handleToggleItem(item.id)}
                        >
                            {togglingItems.has(item.id) ? (
                                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            ) : item.completion ? (
                                <CheckSquare className="h-5 w-5 text-blue-600" />
                            ) : (
                                <Square className="h-5 w-5 text-gray-400" />
                            )}
                            <span className={item.completion ? 'line-through text-gray-500' : ''}>
                                {item.title}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
} 